import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CloudError } from "../cloud/sync";
import type { Item, ItemKind } from "../content/items";
import type { Mode } from "../content/modes";
import type { Ninja } from "../content/ninjas";
import type { BattleResult } from "../game/battle";
import { emptyProgress } from "../game/events";
import {
  applyBattle,
  applyPractice,
  applyTrial,
  baselineEvent,
  battleEvent,
  firstPassOf,
  practiceEvent,
  purchaseEvent,
  rankEvent,
  trialEvent,
  type BattleOutcome,
  type PracticeOutcome,
  type RoundResult,
  type TrialOutcome,
} from "../game/results";
import { balanceOf, nextLevel, setSlot } from "../game/items";
import { isRecruited } from "../game/roster";
import type { TrialSpec } from "../game/trials";
import { defaultSave, type SaveState, type Settings } from "../save/schema";
import { loadStore, persistStore } from "../save/storage";
import {
  activeChild,
  appendEvent,
  editChild,
  emptyStore,
  liveChildren,
  newChild,
  pendingCount,
  progressFor,
  progressOf,
  updateChild,
  viewOf,
  withRetunes,
  type Account,
  type LocalChild,
  type Store,
} from "../save/store";

/** Wait this long after a round before syncing, so several changes go together. */
const SYNC_DELAY_MS = 1500;

export interface SyncStatus {
  syncing: boolean;
  lastSync: string | null;
  pending: number;
  error: CloudError | null;
}

/** What to do with a child that was on this device before signing in. */
export type ImportChoice = { childId: string } & ({ as: "new" } | { as: "merge"; into: string });

export interface StoreActions {
  setMode: (mode: Mode) => void;
  /** Save a child's name, settings and any parent rank changes. */
  saveChild: (
    id: string,
    patch: { name: string; settings: Settings; ranks: Record<Mode, number> },
  ) => void;
  setPin: (id: string, pin: number[] | null) => void;
  recordTrial: (spec: TrialSpec, round: RoundResult, seed: number) => TrialOutcome;
  recordPractice: (mode: Mode, round: RoundResult) => PracticeOutcome;
  recordBattle: (
    battle: { mode: Mode; enemy: Ninja; fighter: Ninja; seconds: number; seed: number },
    result: BattleResult & { durationMs: number | null },
  ) => BattleOutcome;
  /** Choose the ninja to fight with. Ignored unless recruited. */
  setFighter: (id: string) => void;
  /** Buy the next level of an item. False when it's maxed or the coins don't cover it. */
  buy: (item: Item) => boolean;
  /** Fit an item to a slot of a ninja's kit, or empty the slot with null. */
  fitItem: (ninja: Ninja, kind: ItemKind, index: number, id: string | null) => void;
  placed: (ranks: Partial<Record<Mode, number>>) => void;
  skipPlacement: () => void;
  selectChild: (id: string) => void;
  addChild: (name: string) => void;
  removeChild: (id: string) => void;
  importSave: (save: SaveState) => void;
  resetAll: () => void;
  signedIn: (account: Account) => void;
  importLocal: (choices: ImportChoice[]) => void;
  signedOut: () => void;
  familyDeleted: () => void;
  syncNow: () => void;
  /** Choose which strand's look a card shows on Home. */
  wearLook: (rankIndex: number, mode: Mode) => void;
  /** The retune celebration has been seen. */
  dismissPromoted: () => void;
}

/** Children made on this device before signing in that have something worth keeping. */
export function localChildren(store: Store): LocalChild[] {
  return liveChildren(store).filter((c) => !c.synced && (c.base.history.length > 0 || c.name));
}

export function useStore(): {
  store: Store;
  child: LocalChild;
  view: SaveState;
  status: SyncStatus;
  actions: StoreActions;
} {
  const [store, setStore] = useState<Store>(() => withRetunes(loadStore()));
  const latest = useRef(store);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<CloudError | null>(null);

  useEffect(() => {
    latest.current = store;
    persistStore(store);
  }, [store]);

  /** Apply a change now, so anything computed next sees it. */
  const change = useCallback((f: (s: Store) => Store) => {
    const next = f(latest.current);
    latest.current = next;
    setStore(next);
    return next;
  }, []);

  // ─── Sync ────────────────────────────────────────────────────────────────

  const running = useRef(false);
  const again = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncNow = useCallback(() => {
    if (!latest.current.account) return;
    if (running.current) {
      again.current = true;
      return;
    }
    running.current = true;
    setSyncing(true);
    void (async () => {
      try {
        const [{ runSync, mergeSync }, { supabaseApi }] = await Promise.all([
          import("../cloud/sync"),
          import("../cloud/api"),
        ]);
        const result = await runSync(latest.current, supabaseApi);
        change((s) => withRetunes(mergeSync(s, result)));
        setError(result.error);
      } finally {
        running.current = false;
        setSyncing(false);
        if (again.current) {
          again.current = false;
          syncNow();
        }
      }
    })();
  }, [change]);

  const syncSoon = useCallback(() => {
    if (!latest.current.account) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(syncNow, SYNC_DELAY_MS);
  }, [syncNow]);

  useEffect(() => {
    syncNow();
    const onVisible = () => {
      if (document.visibilityState === "visible") syncNow();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", syncNow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", syncNow);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [syncNow]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const current = () => activeChild(latest.current);

  const setMode = useCallback(
    (mode: Mode) => change((s) => updateChild(s, activeChild(s).id, (c) => ({ ...c, mode }))),
    [change],
  );

  const saveChild = useCallback<StoreActions["saveChild"]>(
    (id, { name, settings, ranks }) => {
      change((s) => {
        const c = s.children.find((x) => x.id === id);
        if (!c) return s;
        let next = editChild(s, id, { name, settings });
        const held = progressFor(c).rank;
        for (const [m, r] of Object.entries(ranks) as [Mode, number][])
          if (r !== held[m]) next = appendEvent(next, id, rankEvent(m, r, "parent", settings.ops));
        return next;
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  const setPin = useCallback(
    (id: string, pin: number[] | null) => {
      change((s) => editChild(s, id, { pin }));
      syncSoon();
    },
    [change, syncSoon],
  );

  const recordTrial = useCallback<StoreActions["recordTrial"]>(
    (spec, round, seed) => {
      const c = current();
      const before = progressFor(c);
      const event = trialEvent(
        spec,
        round,
        seed,
        c.settings,
        new Date(),
        firstPassOf(before, spec),
      );
      const { outcome } = applyTrial(before, event);
      change((s) => appendEvent(s, c.id, event));
      syncSoon();
      return outcome;
    },
    [change, syncSoon],
  );

  const recordPractice = useCallback<StoreActions["recordPractice"]>(
    (mode, round) => {
      const c = current();
      const event = practiceEvent(mode, round, c.settings);
      const { outcome } = applyPractice(progressFor(c), event);
      change((s) => appendEvent(s, c.id, event));
      syncSoon();
      return outcome;
    },
    [change, syncSoon],
  );

  const recordBattle = useCallback<StoreActions["recordBattle"]>(
    (battle, result) => {
      const c = current();
      const event = battleEvent(battle, result, c.settings);
      const { outcome } = applyBattle(progressFor(c), event);
      change((s) => appendEvent(s, c.id, event));
      syncSoon();
      return outcome;
    },
    [change, syncSoon],
  );

  const setFighter = useCallback(
    (id: string) => {
      change((s) => {
        const c = activeChild(s);
        if (!isRecruited(progressFor(c).roster, id)) return s;
        return editChild(s, c.id, { settings: { ...c.settings, fighter: id } });
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  const buy = useCallback<StoreActions["buy"]>(
    (item) => {
      const c = current();
      const p = progressFor(c);
      const next = nextLevel(p.items, item);
      if (!next || next.at.price > balanceOf(p)) return false;
      change((s) => appendEvent(s, c.id, purchaseEvent(item.id, next.level, next.at.price)));
      syncSoon();
      return true;
    },
    [change, syncSoon],
  );

  const fitItem = useCallback<StoreActions["fitItem"]>(
    (ninja, kind, index, id) => {
      change((s) => {
        const c = activeChild(s);
        const loadout = setSlot(c.settings.loadout, ninja, kind, index, id);
        if (loadout === c.settings.loadout) return s;
        return editChild(s, c.id, { settings: { ...c.settings, loadout } });
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  const placed = useCallback(
    (ranks: Partial<Record<Mode, number>>) => {
      const id = current().id;
      change((s) => {
        let next = updateChild(s, id, (c) => ({ ...c, onboarded: true }));
        for (const [m, r] of Object.entries(ranks) as [Mode, number][])
          next = appendEvent(next, id, rankEvent(m, r, "placement", current().settings.ops));
        return next;
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  const skipPlacement = useCallback(
    () => change((s) => updateChild(s, activeChild(s).id, (c) => ({ ...c, onboarded: true }))),
    [change],
  );

  const selectChild = useCallback(
    (id: string) => change((s) => ({ ...s, activeChildId: id })),
    [change],
  );

  const addChild = useCallback(
    (name: string) => {
      change((s) => {
        const c = { ...newChild(name, defaultSave().settings), synced: !!s.account };
        return { ...s, children: [...s.children, c] };
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  const removeChild = useCallback(
    (id: string) => {
      change((s) => {
        const c = s.children.find((x) => x.id === id);
        if (!c) return s;
        const children = c.synced
          ? s.children.map((x) => (x.id === id ? { ...x, removed: true, dirty: true } : x))
          : s.children.filter((x) => x.id !== id);
        const next = { ...s, children };
        return liveChildren(next).length
          ? { ...next, activeChildId: activeChild(next).id }
          : { ...emptyStore(), deviceId: s.deviceId, account: s.account, cursor: s.cursor };
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  /** A progress file: merged into a synced child, or replacing a local one's progress. */
  const importSave = useCallback(
    (save: SaveState) => {
      const id = current().id;
      const progress = progressOf(save);
      change((s) => {
        const c = s.children.find((x) => x.id === id)!;
        if (c.synced) return appendEvent(s, id, baselineEvent(progress));
        return updateChild(s, id, (x) => ({
          ...x,
          name: save.name,
          settings: save.settings,
          mode: save.mode,
          onboarded: true,
          base: progress,
        }));
      });
      syncSoon();
    },
    [change, syncSoon],
  );

  /** Signed-out only: clear this child's progress. */
  const resetAll = useCallback(
    () =>
      change((s) =>
        updateChild(s, activeChild(s).id, (c) =>
          c.synced ? c : { ...c, base: emptyProgress(), onboarded: true },
        ),
      ),
    [change],
  );

  const signedIn = useCallback(
    (account: Account) => {
      // An untouched starter profile is dropped by importLocal, once the
      // family has arrived to replace it.
      change((s) => ({ ...s, account }));
      syncNow();
    },
    [change, syncNow],
  );

  /** After signing in: bring this device's children into the family. */
  const importLocal = useCallback(
    (choices: ImportChoice[]) => {
      change((s) => {
        let next = s;
        for (const choice of choices) {
          const c = next.children.find((x) => x.id === choice.childId);
          if (!c || c.synced) continue;
          const baseline = baselineEvent(c.base);
          const hasProgress =
            c.base.history.length > 0 || Object.values(c.base.rank).some((r) => r > 0);
          if (choice.as === "new") {
            next = updateChild(next, c.id, (x) => ({
              ...x,
              name: x.name || "Ninja",
              synced: true,
              dirty: true,
              base: emptyProgress(),
              outbox: hasProgress ? [baseline] : [],
            }));
          } else {
            if (hasProgress) next = appendEvent(next, choice.into, baseline);
            next = {
              ...next,
              children: next.children.filter((x) => x.id !== c.id),
              activeChildId: next.activeChildId === c.id ? choice.into : next.activeChildId,
            };
          }
        }
        // Drop any empty local child left behind.
        const keep = next.children.filter((x) => x.synced || x.base.history.length > 0);
        return keep.length
          ? { ...next, children: keep, activeChildId: activeChild({ ...next, children: keep }).id }
          : next;
      });
      syncNow();
    },
    [change, syncNow],
  );

  /** Signing out leaves this device with local play only. */
  const signedOut = useCallback(() => {
    change((s) => {
      const local = s.children.filter((c) => !c.synced && !c.removed);
      const fresh = emptyStore();
      return local.length
        ? { ...fresh, deviceId: s.deviceId, children: local, activeChildId: local[0]!.id }
        : { ...fresh, deviceId: s.deviceId };
    });
  }, [change]);

  const familyDeleted = useCallback(() => change(() => emptyStore()), [change]);

  const wearLook = useCallback(
    (rankIndex: number, mode: Mode) =>
      change((s) => {
        const c = activeChild(s);
        if (progressFor(c).rank[mode] < rankIndex) return s;
        const look = { ...c.settings.look, [String(rankIndex)]: mode };
        return editChild(s, c.id, { settings: { ...c.settings, look } });
      }),
    [change],
  );

  const dismissPromoted = useCallback(
    () => change((s) => updateChild(s, activeChild(s).id, (c) => ({ ...c, celebrate: [] }))),
    [change],
  );

  const actions = useMemo<StoreActions>(
    () => ({
      setMode,
      saveChild,
      setPin,
      recordTrial,
      recordPractice,
      recordBattle,
      setFighter,
      buy,
      fitItem,
      placed,
      skipPlacement,
      selectChild,
      addChild,
      removeChild,
      importSave,
      resetAll,
      signedIn,
      importLocal,
      signedOut,
      familyDeleted,
      syncNow,
      wearLook,
      dismissPromoted,
    }),
    [
      setMode,
      saveChild,
      setPin,
      recordTrial,
      recordPractice,
      recordBattle,
      setFighter,
      buy,
      fitItem,
      placed,
      skipPlacement,
      selectChild,
      addChild,
      removeChild,
      importSave,
      resetAll,
      signedIn,
      importLocal,
      signedOut,
      familyDeleted,
      syncNow,
      wearLook,
      dismissPromoted,
    ],
  );

  const child = activeChild(store);
  const view = useMemo(() => viewOf(child), [child]);
  const status: SyncStatus = {
    syncing,
    lastSync: store.lastSync,
    pending: pendingCount(store),
    error,
  };
  return { store, child, view, status, actions };
}
